import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import { Helmet } from 'react-helmet';

const PrivacyPolicyPage: React.FC = () => {
    const navigate = useNavigate();

    const navigateToSection = (sectionId: string) => {
        navigate(`/#${sectionId}`);
    };

    const startNewChat = () => {
        navigate('/chat');
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.homeContainer}>
            <Helmet>
                <meta charSet="UTF-8" />
                <title>Политика конфиденциальности - GPT4Chats</title>
                <meta
                    name="description"
                    content="Политика конфиденциальности GPT4Chats: как мы собираем, используем и защищаем ваши данные при работе с ИИ-помощником на базе GPT-4."
                />
                <meta
                    name="keywords"
                    content="политика конфиденциальности, GPT4Chats, защита данных, ИИ-помощник, безопасность, конфиденциальность, GPT-4"
                />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://gpt4chats.ru/privacy-policy" />
                <meta property="og:title" content="Политика конфиденциальности - GPT4Chats" />
                <meta
                    property="og:description"
                    content="Узнайте, как GPT4Chats обеспечивает безопасность и конфиденциальность ваших данных при использовании ИИ-помощника."
                />
                <meta property="og:url" content="https://gpt4chats.ru/privacy-policy" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="GPT4Chats" />
            </Helmet>
            <header className={styles.homeHeader}>
                <div className={styles.container}>
                    <div className={styles.headerContent}>
                        <div className={styles.homeLogo}>
                            <h1>
                                GPT4Chats<span>.</span>
                            </h1>
                        </div>
                        <nav className={styles.homeNav}>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('benefits')}>
                                Преимущества
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('use-cases')}>
                                Примеры
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                            <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                                Начать
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            <section className={styles.privacySection}>
                <div className={styles.container}>
                    <h1>Политика конфиденциальности</h1>
                    <p className={styles.introText}>
                        Настоящая Политика конфиденциальности описывает, как GPT4Chats собирает, использует и защищает вашу личную информацию при использовании нашего сервиса. Мы стремимся обеспечить конфиденциальность и безопасность ваших данных.
                    </p>

                    <div className={styles.policyContent}>
                        <h2>1. Сбор информации</h2>
                        <p>
                            Мы собираем только ту информацию, которую вы добровольно предоставляете при использовании сервиса. Это может включать:
                        </p>
                        <ul>
                            <li>Текст сообщений, введённый вами в чате.</li>
                            <li>Настройки пользователя (например, выбор языка или модели).</li>
                            <li>Технические данные (IP-адрес, тип устройства, браузер) для аналитики и улучшения работы сервиса.</li>
                        </ul>
                        <p>
                            Мы не собираем данные, которые не связаны с предоставлением услуг, и не требуем от вас предоставления избыточной информации.
                        </p>

                        <h2>2. Использование информации</h2>
                        <p>
                            Собранная информация используется исключительно для следующих целей:
                        </p>
                        <ul>
                            <li>Обеспечение работы сервиса и генерации ответов.</li>
                            <li>Персонализация вашего опыта (например, запоминание предпочтений).</li>
                            <li>Улучшение функциональности и производительности GPT4Chats.</li>
                            <li>Анализ использования для выявления ошибок и оптимизации.</li>
                        </ul>
                        <p>
                            Мы не передаём вашу информацию третьим лицам, за исключением случаев, предусмотренных законом.
                        </p>

                        <h2>3. Защита данных</h2>
                        <p>
                            Мы принимаем все необходимые меры для защиты ваших данных, включая:
                        </p>
                        <ul>
                            <li>Шифрование данных при передаче (SSL/TLS).</li>
                            <li>Использование защищённых серверов для хранения информации.</li>
                            <li>Ограничение доступа к данным только уполномоченным сотрудникам.</li>
                        </ul>
                        <p>
                            В случае утечки данных мы обязуемся уведомить вас в течение 72 часов и принять меры для минимизации последствий.
                        </p>

                        <h2>4. Ваши права</h2>
                        <p>
                            Вы имеете право:
                        </p>
                        <ul>
                            <li>Запросить доступ к вашим данным.</li>
                            <li>Требовать удаления вашей информации из наших систем.</li>
                            <li>Ограничить обработку данных в определённых случаях.</li>
                        </ul>
                        <p>
                            Для реализации этих прав свяжитесь с нами по адресу: support@gpt4chats.ru.
                        </p>

                        <h2>5. Использование файлов cookie</h2>
                        <p>
                            Мы используем файлы cookie для:
                        </p>
                        <ul>
                            <li>Сохранения ваших настроек.</li>
                            <li>Анализа трафика и поведения пользователей.</li>
                        </ul>
                        <p>
                            Вы можете отключить cookie в настройках браузера, но это может повлиять на функциональность сервиса.
                        </p>

                        <h2>6. Изменения в политике</h2>
                        <p>
                            Мы можем обновлять эту Политику конфиденциальности. Все изменения будут опубликованы на этой странице, а при значительных изменениях мы уведомим вас через email или уведомление в сервисе.
                        </p>

                        <h2>7. Контакты</h2>
                        <p>
                            Если у вас есть вопросы по поводу этой политики, пожалуйста, свяжитесь с нами:
                        </p>
                        <ul>
                            <li>Email: support@gpt4chats.ru</li>
                        </ul>
                    </div>

                    <div className={styles.policyFooter}>
                        <button className={styles.footerLink} onClick={() => navigate('/')}>
                            На главную
                        </button>
                        <span className={styles.footerSep}>•</span>
                        <button
                            className={styles.footerLink}
                            onClick={() => navigate('/terms-of-use')}
                        >
                            Условия использования
                        </button>
                    </div>
                </div>
            </section>

            <footer className={styles.homeFooter}>
                <div className={styles.container}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerSection}>
                            <h4>
                                GPT4Chats<span>.</span>
                            </h4>
                            <p>Ваш ИИ-помощник для работы и творчества.</p>
                            <p>GPT4Chats.ru</p>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Навигация</h4>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Документы</h4>
                            <button className={styles.navLink} onClick={() => navigate('/privacy-policy')}>
                                Политика конфиденциальности
                            </button>
                            <button className={styles.navLink} onClick={() => navigate('/terms-of-use')}>
                                Условия использования
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Контакты</h4>
                            <p>Email: support@gpt4chats.ru</p>
                        </div>
                    </div>
                    <div className={styles.footerBottom}>
                        <p>© {new Date().getFullYear()} GPT4Chats. Все права защищены.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicyPage;